// ═══════════════════════════════════════════════════
// App Router — The nervous system of the dashboard
// Every route lazy-loaded, every error caught,
// every authenticated route guarded
// ═══════════════════════════════════════════════════

import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  Navigate,
  type RouteObject,
} from 'react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { AuthGuard } from '@/components/auth/AuthGuard';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';

// ── Lazy-loaded Pages ────────────────────────────
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const PropertyDetailPage = lazy(() => import('@/pages/property/PropertyDetailPage'));
const PropertiesPage = lazy(() => import('@/pages/property/PropertiesPage'));
const LiveScanPage = lazy(() => import('@/pages/property/LiveScanPage'));
const AwardsPage = lazy(() => import('@/pages/awards/AwardsPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const SecuritySettings = lazy(() => import('@/pages/settings/SecuritySettings'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const AccountSettings = lazy(() => import('@/pages/settings/AccountSettings'));
const TeamSettings = lazy(() => import('@/pages/settings/TeamSettings'));
const NotificationSettings = lazy(() => import('@/pages/settings/NotificationSettings'));
const BillingSettings = lazy(() => import('@/pages/settings/BillingSettings'));
// Placeholder for pages still being built
const PlaceholderPage = lazy(() => import('@/pages/PlaceholderPage'));
const LegislativePage = lazy(() => import('@/pages/legislation/LegislativePage'));
const FindingsPage = lazy(() => import('@/pages/findings/FindingsPage'));

// ── Onboarding (no auth) ──
const ScanEntryPage = lazy(() => import('@/pages/onboarding/ScanEntryPage'));
const LiveScanProgress = lazy(() => import('@/pages/onboarding/LiveScanProgress'));
const ScanResultsPage = lazy(() => import('@/pages/onboarding/ScanResultsPage'));
const AcceptInvitePage = lazy(() => import('@/pages/auth/AcceptInvitePage'));


// ── Routes ───────────────────────────────────────

const routes: RouteObject[] = [
  // Auth routes (public)
  // Onboarding flow (no auth required)
  { path: '/scan', element: <ScanEntryPage /> },
  { path: '/scan/live/:scanId', element: <LiveScanProgress /> },
  { path: '/scan/results/:scanId', element: <ScanResultsPage /> },
  { path: '/invite/accept', element: <Suspense fallback={<div />}><AcceptInvitePage /></Suspense> },

  {
    path: '/login',
    element: (
      <ErrorBoundary level="app">
        <Suspense fallback={<div />}>
          <LoginPage />
        </Suspense>
      </ErrorBoundary>
    ),
  },

  // Dashboard routes (authenticated, guarded)
  {
    path: '/',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    errorElement: (
      <ErrorBoundary level="app">
        <div style={{ padding: 40, textAlign: 'center', color: '#9B9BC8' }}>
          Something unexpected happened. Please refresh the page.
        </div>
      </ErrorBoundary>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'properties', element: <PropertiesPage /> },
      { path: 'property/:id', element: <PropertyDetailPage /> },
      { path: 'property/:id/findings', element: <PlaceholderPage /> },
      { path: 'property/:id/scans', element: <PlaceholderPage /> },
      { path: 'scans', element: <PlaceholderPage /> },
      { path: 'scans/new', element: <LiveScanPage /> },
      { path: 'findings', element: <FindingsPage /> },
      { path: 'legislation', element: <LegislativePage /> },
      { path: 'awards', element: <AwardsPage /> },
      {
        path: 'settings',
        element: <SettingsPage />,
        children: [
          { index: true, element: <Navigate to="/settings/account" replace /> },
          { path: 'account', element: <AccountSettings /> },
          { path: 'team', element: <TeamSettings /> },
          { path: 'billing', element: <BillingSettings /> },
          { path: 'security', element: <SecuritySettings /> },
          { path: 'notifications', element: <NotificationSettings /> },
        ],
      },
    ],
  },

  // Catch-all
  { path: '*', element: <Navigate to="/dashboard" replace /> },
];

export const router = createBrowserRouter(routes);
